import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Navigate } from "@tanstack/react-router";
import { DEFAULT_ROUTE } from "pages/app/AccountManagementPage";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import toast from "components/ui/toast/Toast";
import { useCompanyCognismToken } from "lib/hooks/api/company/useCompanyCognismToken";
import { useDeleteCompanyCognismToken } from "lib/hooks/api/company/useDeleteCompanyCognismToken";
import { useUpdateCompanyCognismToken } from "lib/hooks/api/company/useUpdateCompanyCognismToken";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useSubscription } from "lib/hooks/subscription";
import { useCognismByot } from "lib/hooks/useCognismByot";

type FormValues = { token: string };

function CognismTokenPage(): JSX.Element {
  const isCognismByotEnabled = useCognismByot();
  const { hasDataTypes } = useSubscription();
  const { data: currentUser } = useCurrentUser();
  const isCompanyAdmin = currentUser?.isCompanyAdmin || currentUser?.isAdmin;
  const canView = isCognismByotEnabled && !!isCompanyAdmin && hasDataTypes("CONTACTS");

  const { data, isLoading } = useCompanyCognismToken({ enabled: canView });
  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: { token: "" },
  });

  const updateMutation = useUpdateCompanyCognismToken({
    onSuccess: () => {
      toast.success("Cognism token saved");
      reset({ token: "" });
    },
    onError: () => {
      toast.error("Couldn't save the Cognism token. Please try again.");
    },
  });

  const deleteMutation = useDeleteCompanyCognismToken({
    onSuccess: () => toast.success("Reverted to the default Cognism token"),
    onError: () => {
      toast.error("Couldn't remove the Cognism token. Please try again.");
    },
  });

  if (!canView) {
    return <Navigate to={DEFAULT_ROUTE} replace />;
  }

  const isCustomerToken = data?.source === "CUSTOMER";

  // We replace the token regardless of who manages it
  const hasToken = !!data?.source;

  const onSubmit = handleSubmit((values) => {
    updateMutation.mutate({ UpdateCognismTokenRequest: { token: values.token.trim() } });
  });

  return (
    <Page>
      <Text h1>Cognism token</Text>
      <Description>
        By default, contact searches use Stotles' generated token and are billed to your
        subscription. Add your own Cognism API token to use your company's Cognism account instead.
        The token applies to everyone in your company.
      </Description>

      {!isLoading && (
        <Status>
          {data?.source === "CUSTOMER" && (
            <Text>
              Your own Cognism token is in use (ending <strong>••••{data.lastFour}</strong>).
            </Text>
          )}
          {data?.source === "STOTLES" && (
            <Text>
              Stotles manages a Cognism token for your company. You can replace it with your own
              token to bill Cognism usage to your account.
            </Text>
          )}
          {!data?.source && (
            <Text>
              Contact searches use Stotles' shared Cognism token. Add your own token to bill usage
              to your company's Cognism account.
            </Text>
          )}
        </Status>
      )}

      <Form onSubmit={onSubmit}>
        <Flex column gap={6}>
          <label htmlFor="token">{hasToken ? "Replace token" : "Cognism API token"}</label>
          <Input
            control={control}
            name="token"
            type="password"
            autoComplete="off"
            placeholder="e.g. API-x-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            rules={{ required: true }}
          />
        </Flex>

        <Flex gap={8}>
          <Button
            type="submit"
            loading={updateMutation.isLoading}
            disabled={!formState.isDirty || deleteMutation.isLoading}
          >
            {hasToken ? "Replace token" : "Save token"}
          </Button>
          {isCustomerToken && (
            <Button
              buttonType="secondary"
              buttonStyle="destructive"
              loading={deleteMutation.isLoading}
              disabled={updateMutation.isLoading}
              onClick={() => deleteMutation.mutate({})}
            >
              Remove token
            </Button>
          )}
        </Flex>
      </Form>
    </Page>
  );
}

export default CognismTokenPage;

const Page = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 24,
  padding: 24,
  overflow: "auto",
  height: "100%",
  backgroundColor: theme.colors.sysBackgroundDefault,
  width: "-webkit-fill-available",
}));

const Description = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
  maxWidth: 650,
}));

const Status = styled.div({
  padding: "16px 0",
});

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 480,
});
